/**
 * Dashboard / detail metric tile — label + big animated number + optional
 * unit / hint. Spotlight tracking + CountUp baked in for that "live data"
 * feel matching web's marketing site.
 *
 * Pass `animate={false}` when the value is a string (e.g. "¥9.99") to
 * skip the counter and just render it. Numeric values default to CountUp.
 */

import type { ReactNode } from 'react';
import { CountUp, SpotlightCard } from '@/components/effects/index.js';

export type MetricTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const TONE_VALUE: Record<MetricTone, string> = {
  neutral: 'text-slate-100',
  info: 'text-shark-200',
  success: 'text-emerald-300',
  warning: 'text-amber-300',
  danger: 'text-rose-300',
};

const TONE_BORDER: Record<MetricTone, string> = {
  neutral: 'border-white/10',
  info: 'border-shark-700/40',
  success: 'border-emerald-800/40',
  warning: 'border-amber-800/40',
  danger: 'border-rose-800/40',
};

const TONE_SPOTLIGHT: Record<MetricTone, `rgba(${number}, ${number}, ${number}, ${number})`> = {
  neutral: 'rgba(148, 163, 184, 0.18)',
  info: 'rgba(96, 165, 250, 0.22)',
  success: 'rgba(52, 211, 153, 0.22)',
  warning: 'rgba(252, 211, 77, 0.22)',
  danger: 'rgba(251, 113, 133, 0.22)',
};

interface Props {
  label: string;
  value: string | number;
  unit?: string;
  tone?: MetricTone;
  hint?: ReactNode;
  onClick?: () => void;
  /** Disable CountUp animation (string values, percentages with formatting). */
  animate?: boolean;
}

export function MetricCard({
  label,
  value,
  unit,
  tone = 'neutral',
  hint,
  onClick,
  animate = true,
}: Props): JSX.Element {
  const numericValue = typeof value === 'number' ? value : null;
  const showCounter = animate && numericValue !== null;

  const inner = (
    <>
      <div className="text-[10px] text-slate-500 uppercase tracking-[0.14em] font-medium">
        {label}
      </div>
      {/*
        min-w-0 + truncate 是为了让 flex item 在窄屏下尊重 truncate 而不是
        撑爆容器. text-2xl → md:text-3xl 响应式: 移动端给数字小一号留余地,
        防 7+ 位数字 (大额订单总数 / 累计答题量) 在 <380px 屏换行或溢出.
      */}
      <div className="mt-1.5 flex items-baseline gap-1 min-w-0">
        <span
          className={`font-display text-2xl md:text-3xl font-bold tabular-nums truncate ${TONE_VALUE[tone]}`}
        >
          {showCounter ? <CountUp to={numericValue} /> : value}
        </span>
        {unit !== undefined && (
          <span className="text-xs text-slate-500 font-medium shrink-0">{unit}</span>
        )}
      </div>
      {hint !== undefined && (
        <div className="text-[10px] text-slate-500 mt-2">{hint}</div>
      )}
    </>
  );

  const interactive = onClick !== undefined;
  const surface =
    `mat-matte border rounded-xl px-4 py-3.5 ${TONE_BORDER[tone]} ` +
    `transition-all duration-200 ${
      interactive
        ? 'cursor-pointer hover:border-shark-500/50 hover:-translate-y-0.5'
        : ''
    }`;

  // Use a div with role=button when clickable so we keep SpotlightCard's
  // mouse-tracking — nested <SpotlightCard><button> would either break the
  // pointer events or require duplicating the surface chrome. Trade-off:
  // keyboard activation handled manually via onKeyDown.
  const handleKeyDown = interactive
    ? (e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }
    : undefined;

  return (
    <SpotlightCard
      className={surface}
      spotlightColor={TONE_SPOTLIGHT[tone]}
      intensity={0.55}
    >
      {interactive ? (
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          className="outline-none focus-visible:ring-2 focus-visible:ring-shark-400 rounded"
        >
          {inner}
        </div>
      ) : (
        inner
      )}
    </SpotlightCard>
  );
}
